import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Toggle } from '@guildeducationinc/recess';
import { PaymentRequestStatus } from '@src/types';
import { getFullName } from '@src/utilities/nameHelper';
import { sortBy } from 'lodash';
import { LanguagesIcon } from 'lucide-react';
import { DataTable, LoaderCircleIcon, Messages, MessageType, TrackableButton, TRANSLATE_COMMENT, useAlert, useLocale, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import styles from './ClaimsCommentsList.module.scss';
const ENGLISH_LOCALE = 'en';
export const ClaimsCommentsList = ({ comments, error, loading, onTranslate, }) => {
    const { t, formatToLocalDatetime } = useLocale();
    const { showAlert } = useAlert();
    const [showInEnglish, setShowInEnglish] = useState(true);
    const [commentIdToTranslate, setCommentIdToTranslate] = useState(undefined);
    const [translate] = useMutation(TRANSLATE_COMMENT, {
        variables: {
            id: 0,
        },
    });
    const sortedComments = useMemo(() => {
        return sortBy(comments, c => c.comment.createdAt ?? 0);
    }, [comments]);
    const isEmployeeNonEnglish = useMemo(() => {
        return comments.some(comment => !comment.empLocale?.toLowerCase().startsWith(ENGLISH_LOCALE));
    }, [comments]);
    const handleTranslate = async (id) => {
        try {
            setCommentIdToTranslate(id);
            await translate({ variables: { id } });
            onTranslate?.();
        }
        catch (e) {
            showAlert(t('Error translating comment'), 'error');
        }
        finally {
            setCommentIdToTranslate(undefined);
        }
    };
    const showTranslateButton = (requestComment) => {
        if (!requestComment.isEmployee || // No need to translate if the comment is not from an employee
            !requestComment.empLocale || // No need to translate if the user locale is not set
            requestComment.comment.extraData?.translatedText // Already translated
        ) {
            return false;
        }
        if (requestComment.empLocale.toLowerCase().startsWith(ENGLISH_LOCALE)) {
            return false;
        }
        return true;
    };
    const getMessage = (requestComment, inEnglish) => {
        if (inEnglish &&
            requestComment.comment.extraData?.detectedLanguage !== ENGLISH_LOCALE &&
            requestComment.comment.extraData?.translatedText) {
            return requestComment.comment.extraData.translatedText;
        }
        if (!inEnglish && requestComment.comment.extraData?.detectedLanguage === ENGLISH_LOCALE) {
            return requestComment.comment.extraData.translatedText;
        }
        return requestComment.comment.message;
    };
    const getType = (requestComment) => {
        if (requestComment.comment.isInternal) {
            return t('Internal note');
        }
        switch (requestComment.status) {
            case PaymentRequestStatus.READY_FOR_PAYMENT:
                return t('Approved');
            case PaymentRequestStatus.REJECTED:
                return t('Rejected');
            case PaymentRequestStatus.INFO_REQUESTED:
                return t('Request info');
        }
        return t('In-review');
    };
    return (_jsxs("div", { children: [error && (_jsx(Messages, { messages: [{ type: MessageType.ERROR, value: t('Failed to fetch comments') }] })), isEmployeeNonEnglish && (_jsx("div", { className: 'flex justify-end gap-2', children: _jsx(Toggle, { label: t('View in English'), active: showInEnglish, onToggle: () => setShowInEnglish(!showInEnglish) }) })), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, getRowHeight: () => 'auto', loading: loading, rows: sortedComments, pageSizeOptions: [], getRowId: row => row.comment.id, hideFooter: true, columns: [
                    {
                        field: 'comment.createdAt',
                        headerName: t('Created'),
                        width: 120,
                        renderCell: ({ row }) => formatToLocalDatetime(row.comment.createdAt, { dateStyle: 'medium' }),
                    },
                    {
                        field: 'type',
                        headerName: t('Type'),
                        width: 150,
                        renderCell: ({ row }) => getType(row),
                    },
                    {
                        field: 'comment.createdBy',
                        headerName: t('From'),
                        flex: 0.3,
                        renderCell: ({ row }) => `${row.comment.createdBy ? getFullName(row.comment.createdBy) : '-'}`,
                    },
                    {
                        field: 'message',
                        headerName: t('Content'),
                        flex: 0.8,
                        renderCell: ({ row }) => (_jsxs("div", { className: 'flex justify-between gap-2 w-full', children: [_jsx("div", { className: styles.comment, dangerouslySetInnerHTML: {
                                        __html: getMessage(row, showInEnglish) ?? '',
                                    } }), showTranslateButton(row) && (_jsx("div", { children: _jsx(TrackableButton, { enText: 'Translate', label: t('Translate'), tag: 'payment_request', onClick: () => handleTranslate(row.comment.id), disabled: !!commentIdToTranslate, btnType: 'icon', icon: commentIdToTranslate === row.comment.id ? (_jsx(LoaderCircleIcon, { fontSize: 'small' })) : (_jsx(LanguagesIcon, { size: 18 })), appearance: 'ghost', showTooltipForIcon: true }) }))] })),
                    },
                ] })] }));
};
